import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { memo } from 'react';

import { SymbolContext } from '@flipster/features/src/shared/symbol/bunja';
import { css } from '@flipster/styled-system/css';
import { TableRow } from '@flipster/ui/Table';

import SymbolSideLeverageCell from './cells/1-symbol';
import QuantitySizeCell from './cells/2-quantity-size';
import PnlCell from './cells/3-pnl';
import PriceCell from './cells/4-price';
import MarginCell from './cells/5-margin';
import TpSlCell from './cells/6-tpsl';
import AdjustLeverageCell from './cells/7-adjust-leverage';
import CtaCell from './cells/cta';
import { PositionSlotContext, openPositionRowDataBunja } from './row-bunja';

interface Props {
  symbol: string;
  slot: number;
  index: number;
}

/**
 * 스코프 Provider(symbol, slot) 주입 + 셀 레이아웃만 담당한다.
 * 데이터는 각 셀이 openPositionRowDataBunja의 필드 atom을 직접 consume하므로,
 * 소켓 tick에 이 컴포넌트는 리렌더되지 않고 값이 실제로 바뀐 셀만 리렌더된다.
 */
const OpenPositionRow = memo(({ symbol, slot, index }: Props) => (
  <SymbolContext.Provider value={symbol}>
    <PositionSlotContext.Provider value={slot}>
      <OpenPositionRowContent index={index} />
    </PositionSlotContext.Provider>
  </SymbolContext.Provider>
));
OpenPositionRow.displayName = 'OpenPositionRow';

const OpenPositionRowContent = ({ index }: { index: number }) => {
  // 포지션 청산/스냅샷 미로드 프레임에만 행 전체를 내린다 (boolean이라 tick에는 전파 없음)
  const { isReadyAtom } = useBunja(openPositionRowDataBunja);
  const isReady = useAtomValue(isReadyAtom);

  if (!isReady) return null;

  return (
    <TableRow className={css({ '& td': { borderRadius: '0px!' } })}>
      <SymbolSideLeverageCell index={index} />
      <QuantitySizeCell />
      <PnlCell />
      <PriceCell field='entryPrice' />
      <PriceCell field='markPrice' />
      <PriceCell field='liquidationPrice' zeroString='-' />
      <MarginCell />
      <TpSlCell tpSlType='tp' />
      <TpSlCell tpSlType='sl' />
      <AdjustLeverageCell />
      <CtaCell index={index} />
    </TableRow>
  );
};

export default OpenPositionRow;
